import { createSegment } from '../defaults'
import type { NodeType, Segment } from '../types'

const cloneSegment = (segment: Segment): Segment => structuredClone(segment)

export function nodeTypeOptionsForPosition(index: number, simple = false): NodeType[] {
  return index === 0 || simple
    ? ['i2v', 'flf2v']
    : ['flf2v', 'svi', 'sviflf']
}

export function nodeTypeForPosition(nodeType: NodeType, index: number): NodeType {
  if (index === 0) {
    if (nodeType === 'svi') return 'i2v'
    if (nodeType === 'sviflf') return 'flf2v'
    return nodeType
  }
  if (nodeType === 'i2v') return 'svi'
  return nodeType
}

export function normalizeSegmentPositions(segments: Segment[]): Segment[] {
  return segments.map((segment, index) => ({
    ...segment,
    node_type: nodeTypeForPosition(segment.node_type, index),
  }))
}

function adjustFormerAndNewLast(segments: Segment[], oldLastId: string): Segment[] {
  if (segments.length < 2) return segments
  return segments.map((segment, index) => {
    const isOldLast = segment.id === oldLastId
    const isNewLast = index === segments.length - 1
    if (isOldLast && !isNewLast && segment.node_type === 'sviflf') {
      return { ...segment, node_type: 'svi' as NodeType }
    }
    if (isNewLast && !isOldLast && segment.node_type === 'svi') {
      return { ...segment, node_type: 'sviflf' as NodeType }
    }
    return segment
  })
}

export function moveSegment(segments: Segment[], index: number, direction: -1 | 1): Segment[] {
  const target = index + direction
  if (target < 0 || target >= segments.length) return segments
  const next = segments.map(cloneSegment)
  const [moved] = next.splice(index, 1)
  next.splice(target, 0, moved)
  return normalizeSegmentPositions(next)
}

export function insertSegmentAfter(segments: Segment[], index: number): Segment[] {
  const oldLastId = segments.at(-1)?.id ?? ''
  const next = segments.map(cloneSegment)
  next.splice(index + 1, 0, createSegment('svi'))
  return normalizeSegmentPositions(adjustFormerAndNewLast(next, oldLastId))
}

export function duplicateSegment(segments: Segment[], index: number): Segment[] {
  const oldLastId = segments.at(-1)?.id ?? ''
  const copy = cloneSegment(segments[index])
  copy.id = crypto.randomUUID()
  delete copy.end_image
  const next = segments.map(cloneSegment)
  next.splice(index + 1, 0, copy)
  return normalizeSegmentPositions(adjustFormerAndNewLast(next, oldLastId))
}

export function removeSegment(segments: Segment[], index: number): Segment[] {
  if (segments.length <= 2) return segments
  const oldLastId = segments.at(-1)?.id ?? ''
  const next = segments.filter((_, current) => current !== index).map(cloneSegment)
  return normalizeSegmentPositions(adjustFormerAndNewLast(next, oldLastId))
}
